import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
// biome-ignore lint/nursery/noImportCycles: Intentional cycle for recursive form rendering
import { ArrayField } from "../components/internal/array-field.js";
import { DiscriminatorSelect } from "../components/internal/discriminator-select.js";
// biome-ignore lint/nursery/noImportCycles: Intentional cycle for recursive form rendering (default-widgets -> UnionField -> ObjectField -> ShapeField -> default-widgets)
import { UnionField } from "../components/internal/union-field.js";
// biome-ignore lint/nursery/noImportCycles: Intentional cycle for recursive form rendering (default-widgets -> ObjectField -> ShapeField -> default-widgets)
import { ObjectField } from "../components/object-field.js";
import { useAutoDiscriminatorField } from "../hooks/use-discriminator-field.js";
import { useSchemaContext } from "../hooks/use-schema-context.js";
import { getDiscriminatorProperty, resolveRef } from "../utils/schema-context.js";
import { FieldWrapper } from "./field-wrapper.js";
/**
 * Generate a content preview for an object value.
 * Finds the first string property and returns its value truncated to maxLength.
 */
export function getContentPreview(value, schema, maxLength = 50) {
    if (!value || typeof value !== 'object')
        return '';
    const properties = schema.properties;
    if (!properties)
        return '';
    // Check preferred preview properties first
    for (const preferredKey of ['alias', 'name', 'title']) {
        if (typeof value[preferredKey] === 'string' && value[preferredKey]) {
            return value[preferredKey].substring(0, maxLength);
        }
    }
    // Fall back to first string property
    for (const [key, propSchema] of Object.entries(properties)) {
        if (propSchema.type === 'string' && typeof value[key] === 'string' && value[key]) {
            const str = value[key];
            if (str.length > maxLength) {
                return `${str.substring(0, maxLength - 3)}...`;
            }
            return str;
        }
    }
    return '';
}
// ============================================================================
export { FieldWrapper } from "./field-wrapper.js";
export function TextInput({ value, onChange, onBlur, path, schema, config, error, disabled, readOnly, label, description, required }) {
    return (_jsx(FieldWrapper, { label: label, error: error, description: description, required: required, className: config?.className, children: _jsx("input", { type: "text", name: path, className: `input input-sm w-full ${error ? 'input-error' : ''}`, value: value ?? '', onChange: (e) => onChange(e.target.value), onBlur: onBlur, placeholder: config?.placeholder ?? schema.title ?? '', disabled: disabled, readOnly: readOnly, maxLength: schema.maxLength, minLength: schema.minLength }) }));
}
export function TextArea({ value, onChange, onBlur, path, schema, config, error, disabled, readOnly, label, description, required }) {
    return (_jsx(FieldWrapper, { label: label, error: error, description: description, required: required, className: config?.className, children: _jsx("textarea", { name: path, className: `textarea textarea-sm w-full ${error ? 'textarea-error' : ''}`, value: value ?? '', onChange: (e) => onChange(e.target.value), onBlur: onBlur, placeholder: config?.placeholder ?? schema.title ?? '', disabled: disabled, readOnly: readOnly, rows: 4 }) }));
}
/**
 * Monospace textarea with auto-resize for editing prompts, templates, and code.
 * Uses field-sizing:content to grow with content, with a minimum of 2 rows.
 */
export function PromptTextArea({ value, onChange, onBlur, path, schema, config, error, disabled, readOnly, label, description, required }) {
    return (_jsx(FieldWrapper, { label: label, error: error, description: description, required: required, className: config?.className, children: _jsx("textarea", { name: path, className: `textarea textarea-sm w-full font-mono ${error ? 'textarea-error' : ''}`, style: { fieldSizing: 'content' }, value: value ?? '', onChange: (e) => onChange(e.target.value), onBlur: onBlur, placeholder: config?.placeholder ?? schema.title ?? '', disabled: disabled, readOnly: readOnly, rows: 4 }) }));
}
export function NumberInput({ value, onChange, onBlur, path, schema, config, error, disabled, readOnly, label, description, required }) {
    return (_jsx(FieldWrapper, { label: label, error: error, description: description, required: required, className: config?.className, children: _jsx("input", { type: "number", name: path, className: `input input-sm w-full ${error ? 'input-error' : ''}`, value: value ?? '', onChange: (e) => {
                const val = e.target.value;
                onChange(val === '' ? undefined : Number(val));
            }, onBlur: onBlur, placeholder: config?.placeholder ?? schema.title ?? '', disabled: disabled, readOnly: readOnly, min: schema.minimum, max: schema.maximum, step: schema.multipleOf }) }));
}
export function Checkbox({ value, onChange, path, schema, config, disabled, readOnly, label, description, error }) {
    // Checkbox renders its own label inline, so we use FieldWrapper with showLabel=false
    const tooltipText = description ?? schema.description;
    return (_jsx(FieldWrapper, { error: error, showLabel: false, showDescription: false, children: _jsxs("label", { className: "label cursor-pointer gap-2 text-sm", children: [_jsx("input", { type: "checkbox", name: path, className: "checkbox checkbox-primary", style: { width: '1rem', height: '1rem' }, checked: value ?? false, onChange: (e) => onChange(e.target.checked), disabled: disabled || readOnly }), label && (_jsxs("span", { className: "flex items-center gap-1", children: [label, tooltipText && (_jsx("span", { className: "tooltip tooltip-right", "data-tip": tooltipText, children: _jsx("svg", { className: "w-4 h-4 text-base-content/50 cursor-help", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" }) }) }))] }))] }) }));
}
export function Select({ value, onChange, onBlur, path, schema, config, error, disabled, readOnly, label, description, required }) {
    const options = schema.enum ?? [];
    return (_jsx(FieldWrapper, { label: label, error: error, description: description, required: required, className: config?.className, children: _jsxs("select", { name: path, className: `select select-sm w-full ${error ? 'select-error' : ''}`, value: value ?? '', onChange: (e) => onChange(e.target.value), onBlur: onBlur, disabled: disabled || readOnly, children: [_jsx("option", { value: "", children: config?.placeholder ?? 'Select...' }), options.map((opt) => (_jsx("option", { value: opt, children: opt }, opt)))] }) }));
}
export function Radio({ value, onChange, onBlur, schema, config, path, disabled, readOnly, label, description, error, required }) {
    const options = schema.enum ?? [];
    return (_jsx(FieldWrapper, { label: label, error: error, description: description, required: required, className: config?.className, children: _jsx("div", { className: "flex flex-col gap-1", onBlur: onBlur, role: "radiogroup", "aria-label": label, children: options.map((opt) => (_jsxs("label", { className: "label cursor-pointer justify-start gap-2", children: [_jsx("input", { type: "radio", name: path, className: "radio radio-primary radio-sm", value: opt, checked: value === opt, onChange: () => onChange(opt), disabled: disabled || readOnly }), _jsx("span", { className: "label-text", children: opt })] }, opt))) }) }));
}
export function Slider({ value, onChange, onBlur, path, schema, config, disabled, readOnly, label, description, error, required }) {
    const min = schema.minimum ?? 0;
    const max = schema.maximum ?? 100;
    const step = schema.multipleOf ?? config?.step ?? (schema.type === 'integer' ? 1 : 0.01);
    return (_jsx(FieldWrapper, { label: label, error: error, description: description, required: required, className: config?.className, children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("input", { type: "range", name: path, className: "range range-primary range-sm flex-1", value: value ?? min, onChange: (e) => onChange(Number(e.target.value)), onBlur: onBlur, min: min, max: max, step: step, disabled: disabled || readOnly }), _jsx("span", { className: "text-sm font-mono w-12 text-right", children: value ?? min })] }) }));
}
export function Hidden(_props) {
    return null;
}
/**
 * Widget wrapper for ObjectField. Used when widget="object" is explicitly set.
 * ShapeField normally handles objects directly via ObjectField — this is for the widget registry.
 */
function ObjectFieldWidget({ path, schema, schemaRef, config, disabled, readOnly }) {
    return (_jsx(ObjectField, { path: path, schema: schema, parentRef: schemaRef, config: config, disabled: disabled, readOnly: readOnly }));
}
/**
 * Check if a schema represents a primitive type (not collapsible).
 */
export function isPrimitiveSchema(schema) {
    if (!schema)
        return true;
    const type = schema.type;
    if (Array.isArray(type)) {
        return type.every((t) => ['string', 'number', 'integer', 'boolean', 'null'].includes(t));
    }
    return ['string', 'number', 'integer', 'boolean', 'null'].includes(type);
}
/**
 * Widget for rendering arrays. Delegates to ArrayField.
 */
export function ArrayWidget({ path, schema, config, disabled, readOnly }) {
    return _jsx(ArrayField, { path: path, schema: schema, config: config, disabled: disabled, readOnly: readOnly });
}
/**
 * Widget for rendering discriminated unions (oneOf with const discriminator).
 * Auto-detects the discriminator property and renders variant selector + fields.
 */
export function DiscriminatedUnionWidget({ path, schema, config, disabled, readOnly }) {
    const ctx = useSchemaContext();
    const resolvedSchema = useMemo(() => resolveRef(ctx, schema), [ctx, schema]);
    const discriminator = useAutoDiscriminatorField({ path, schema: resolvedSchema });
    if (!discriminator.discriminatorPath || discriminator.options.length === 0) {
        return _jsx(ObjectField, { path: path, schema: resolvedSchema, config: config, disabled: disabled, readOnly: readOnly });
    }
    return (_jsxs("div", { className: "space-y-0", children: [_jsx(DiscriminatorSelect, { path: path, value: discriminator.value, options: discriminator.options, onChange: discriminator.onChange, disabled: disabled, readOnly: readOnly, size: "md", fullWidth: true }), _jsx(UnionField, { path: path, schema: resolvedSchema, config: config, disabled: disabled, readOnly: readOnly })] }));
}
export function getWidgetForSchema(ctx, schema, widgets) {
    // Check format first for explicit widget selection
    if (schema.format) {
        const formatWidget = widgets[schema.format];
        if (formatWidget)
            return formatWidget;
        if (schema.format === 'textarea')
            return widgets.textarea ?? TextArea;
        if (schema.format === 'radio')
            return widgets.radio ?? Radio;
        if (schema.format === 'slider' || schema.format === 'range')
            return widgets.slider ?? Slider;
    }
    // Discriminated union (oneOf with const discriminator)
    if (schema.oneOf && Array.isArray(schema.oneOf)) {
        const discriminatorProp = getDiscriminatorProperty(ctx, schema);
        if (discriminatorProp) {
            return widgets.discriminatedUnion ?? DiscriminatedUnionWidget;
        }
    }
    // Enum defaults to select (can be overridden with format: 'radio')
    if (schema.enum && Array.isArray(schema.enum))
        return widgets.select ?? Select;
    switch (schema.type) {
        case 'string':
            return widgets.text ?? TextInput;
        case 'number':
        case 'integer':
            return widgets.number ?? NumberInput;
        case 'boolean':
            return widgets.checkbox ?? Checkbox;
        case 'object':
            return widgets.object ?? ObjectFieldWidget;
        case 'array':
            return widgets.array ?? ArrayWidget;
        default:
            return widgets.text ?? TextInput;
    }
}
/**
 * Get widget for scalar types only.
 * Used by ShapeField which handles objects, arrays, and unions directly.
 */
export function getWidgetForScalar(ctx, schema, widgets) {
    // Check format first for explicit widget selection
    if (schema.format) {
        const formatWidget = widgets[schema.format];
        if (formatWidget)
            return formatWidget;
        if (schema.format === 'textarea')
            return widgets.textarea ?? TextArea;
        if (schema.format === 'radio')
            return widgets.radio ?? Radio;
        if (schema.format === 'slider' || schema.format === 'range')
            return widgets.slider ?? Slider;
    }
    // Enum defaults to select (can be overridden with format: 'radio')
    if (schema.enum && Array.isArray(schema.enum))
        return widgets.select ?? Select;
    switch (schema.type) {
        case 'string':
            return widgets.text ?? TextInput;
        case 'number':
        case 'integer':
            return widgets.number ?? NumberInput;
        case 'boolean':
            return widgets.checkbox ?? Checkbox;
        default:
            return widgets.text ?? TextInput;
    }
}
export const defaultWidgets = {
    text: TextInput,
    textarea: TextArea,
    promptTextArea: PromptTextArea,
    number: NumberInput,
    checkbox: Checkbox,
    select: Select,
    radio: Radio,
    slider: Slider,
    hidden: Hidden,
    object: ObjectFieldWidget,
    array: ArrayWidget,
    discriminatedUnion: DiscriminatedUnionWidget
};
